import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { logger } from "@/utils/logger";

interface ConsumerProfile {
  id: string;
  user_id: string;
  full_name: string | null;
  email: string | null;
  phone_number: string | null;
  created_at: string;
  updated_at: string;
}

interface ConsumerContextType {
  consumerProfile: ConsumerProfile | null;
  isConsumer: boolean;
  isLoading: boolean;
  refreshProfile: () => Promise<void>;
}

const ConsumerContext = createContext<ConsumerContextType>({
  consumerProfile: null,
  isConsumer: false,
  isLoading: true,
  refreshProfile: async () => {},
});

export const useConsumer = () => useContext(ConsumerContext);

export const ConsumerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [consumerProfile, setConsumerProfile] = useState<ConsumerProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    if (!user) {
      setConsumerProfile(null);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("consumer_profiles")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) {
        logger.error("Failed to fetch consumer profile", { error: error.message });
      }
      setConsumerProfile(data as ConsumerProfile | null);
    } catch (err: any) {
      logger.error("Consumer profile fetch error", { error: err.message });
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return (
    <ConsumerContext.Provider
      value={{
        consumerProfile,
        isConsumer: !!consumerProfile,
        isLoading,
        refreshProfile: fetchProfile,
      }}
    >
      {children}
    </ConsumerContext.Provider>
  );
};
